import { observable, runInAction } from 'mobx';
import { UnleashClient, IMutableContext } from 'unleash-proxy-client';
import Config from '../Config';
import isNode from 'is-node';
import CookieConsentStore from './CookieConsentStore';

export default class FeatureFlagStore {
  @observable accessor isInitialized: boolean = false;
  public initializationPromise: Promise<void>;
  public readonly APP_NAME_PREFIX = 'tweb-';
  private readonly THIRTY_MINUTES_IN_MILLISECONDS = 1000 * 60 * 30;
  private readonly FEATURE_FLAG_SESSION = 'ff-session-id';
  private readonly FEATURE_FLAG_STORAGE = 'ff-storage';
  private featureFlagsClient: UnleashClient;

  public constructor() {
    if (!isNode && Config.env !== 'ui-test') {
      this.initializationPromise = this.initialize();
    }
  }

  public async initialize() {
    runInAction(() => {
      this.featureFlagsClient = new UnleashClient({
        url: `${Config.teliaWebUrl}/api/feature-flags/proxy`,
        clientKey: 'key',
        appName: `${this.APP_NAME_PREFIX}${Config.env}`,
        environment: Config.env,
        refreshInterval: this.THIRTY_MINUTES_IN_MILLISECONDS,
        storageProvider: {
          save: (_name: string, data) =>
            new Promise<void>((resolve) => {
              resolve(this.setSessionStorageItem(this.FEATURE_FLAG_STORAGE, JSON.stringify(data)));
            }),
          get: () =>
            new Promise<any>((resolve) => {
              resolve(JSON.parse(sessionStorage.getItem(this.FEATURE_FLAG_STORAGE)) || []);
            })
        }
      });
      this.featureFlagsClient.updateContext({ sessionId: this.getFeatureFlagSessionId() });
    });
    await this.featureFlagsClient.start();
    runInAction(() => {
      this.isInitialized = true;
    });
  }

  private getFeatureFlagSessionId = () => {
    if (!sessionStorage.getItem(this.FEATURE_FLAG_SESSION)) {
      const id = btoa(Math.random().toString());
      this.setSessionStorageItem(this.FEATURE_FLAG_SESSION, id);
      return id;
    }
    return sessionStorage.getItem(this.FEATURE_FLAG_SESSION);
  };

  private setSessionStorageItem = (key: string, value: string) => {
    if (CookieConsentStore.isPreferencesCookiesEnabled()) {
      sessionStorage.setItem(key, value);
    }
  };

  public isEnabled = (name: string) => this.featureFlagsClient?.isEnabled(`${this.APP_NAME_PREFIX}${name}`);
  public getVariant = (name: string) => this.featureFlagsClient?.getVariant(`${this.APP_NAME_PREFIX}${name}`);
  public updateContext = (context: IMutableContext) => this.featureFlagsClient?.updateContext(context);
}
